import { LogoOnlyHeader } from '@/features/shared/components/Headers/LogoOnlyHeader'
import { type CMSData } from '@/features/cms/components/CMS/types'
import { ErrorPage } from '@/features/shared/components/ErrorPage'
import { LayoutRenderer } from '@/features/cms/components/CMS/components/Layouts'
import { SEOHead } from '@/features/shared/components/SEOHeader/SEOHead'
import { getCanonicalUrl } from '@/features/shared/utils/seo/seoConfig'
import { usePathname } from 'next/navigation'
import Head from 'next/head'
import { buildCmsStructuredData } from '@/features/shared/utils/seo/buildCmsStructuredData'

export type CMSPageProps = {
  cmsData: CMSData
  useHeaderLayout?: boolean
  hideChat?: boolean
}

export const CMSPage = ({ cmsData, useHeaderLayout, hideChat }: CMSPageProps) => {
  const pathname = usePathname()
  const canonicalUrl = getCanonicalUrl(pathname ?? '/')

  if (typeof cmsData !== 'object' || 'error' in cmsData) {
    return <CMSPageError statusCode={cmsData.error.status} useHeaderLayout={useHeaderLayout} />
  }

  return (
    <>
      <SEOHead
        title={cmsData.seo_title}
        description={cmsData.seo_description}
        canonicalUrl={canonicalUrl}
        doNotIndex={cmsData.do_not_index}
        doNotFollow={cmsData.do_not_follow}
        additionalSchemas={buildCmsStructuredData({ cmsData, canonicalUrl })}
      />
      <LayoutRenderer layoutData={cmsData.content} hideChat={hideChat} />
    </>
  )
}

export const CMSPageError = ({
  statusCode,
  useHeaderLayout,
}: {
  statusCode: number
  useHeaderLayout?: boolean
}) => {
  return (
    <>
      <Head>
        <title>{statusCode || 404} - Shipt</title>
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      {!useHeaderLayout && <LogoOnlyHeader />}
      <ErrorPage statusCode={statusCode} isCMSPage />
    </>
  )
}
